import { Either, Fail, Success } from 'monet';
import LeftResponse from '@/storage/LeftResponse';

/**
 * @description Сотрудник Мис
 * @constructor
 */
export function MisEmployee(id, name, surname, patronymic, externalId) {
  this.id = id;
  this.name = `${name} ${surname} ${patronymic}`;
  this.surname = surname;
  this.patronymic = patronymic;
  this.externalId = externalId;
  this.getId = () => this.id;
  this.getName = () => this.name;
  this.getSurname = () => this.surname;
  this.getPatronymic = () => this.patronymic;
  this.getExternalId = () => this.externalId;
  this.setExternalId = (value) => new this.constructor(id, name, surname, patronymic, value);
}

/**
 * @description Сотрудник Fdoc
 * @constructor
 */
export function FdocEmployee(id, name) {
  this.id = id;
  this.name = name;
  this.externalId = null;
  /**
   * @returns {string}
   */
  this.getExternalId = () => this.id;
  this.getName = () => this.name;
}

/**
 * @description Отправляемый Пакет документов на подписание
 * @constructor
 */
export function SendingPackageForSign(id, name) {
  this.id = id;
  this.name = name;
}

/**
 * @description Шлюз интеграции FDoc.
 * @param {Axios} axios
 */
export default function FDocIntegrationGateway(axios) {
  /**
   * Создание интеграции
   * @param params
   * @returns {*}
   */
  this.createIntegration = (params) => axios
    .post('/api/fdoc-integration', params)
    .then(
      (response) => Either.of(response.data),
      (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );

  /**
   * Отправка логина и пароля сотрудника для получения списка сотрудников мис, fdoc
   * @param params
   * @returns {*}
   */
  this.getFDocMisEmployeeListByPassword = (params) => axios
    .get('/api/fdoc-integration/employees-by-password', { params })
    .then(
      (response) => {
        const misEmployees = response.data.mis_employees.map((el) => new MisEmployee(
          el.id,
          el.name,
          el.surname,
          el.patronymic,
          el.external_id,
        ));
        const fdocEmployees = response.data.fdoc_employees.map((el) => new FdocEmployee(
          el.id,
          el.name,
        ));
        return Either.of({
          misEmployees,
          fdocEmployees,
          title: response.data.title,
        });
      },
      (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );

  /**
   * Получение флагов
   * isIntegrated - имеет ли клиника интеграцию с FDoc
   * isLinked - имеет ли сотруни
   * @returns {*}
   */
  this.getPage = () => axios
    .get('/api/fdoc-integration')
    .then(
      (response) => response.data,
      (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );

  /**
   * @description Связать сотрудника МИС с сотрудником FDoc
   * @param {number} employeeId
   * @param {?number} externalEmployeeId
   * @returns {Promise<Either>}
   */
  this.linkEmployeeFdoc = (employeeId, externalEmployeeId) => axios
    .put(
      `/api/fdoc-integration/employees/${employeeId}`,
      { external_employee_id: externalEmployeeId },
    )
    .then(
      async (response) => Either.right(response.data),
      async (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );
  /**
   * @description Получение сотрундиков МИС и сотрудников F.doc по внешнему идентификатору
   * сотрудника F.doc
   *
   * @param params
   * @returns {*}
   */
  this.getFDocMisEmployeeListByExternalId = (params) => axios
    .get('/api/fdoc-integration/employees-by-external-id', { params })
    .then(
      (response) => {
        const misEmployees = response.data.mis_employees.map((el) => new MisEmployee(
          el.id,
          el.name,
          el.surname,
          el.patronymic,
          el.external_id,
        ));
        const fdocEmployees = response.data.fdoc_employees.map((el) => new FdocEmployee(
          el.id,
          el.name,
        ));
        return Either.of({
          misEmployees,
          fdocEmployees,
          title: response.data.title,
        });
      },
      (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );

  /**
   * @description Создать контракт из шаблона для подписания.
   * @param {number} documentId Идентификатор шаблона.
   * @param {string} number     Номер договора.
   * @returns {Promise}
   */
  this.generateContractFromTemplateForSign = async (documentId, number) => {
    try {
      const response = await axios.get(`/api/contracts/${documentId}/sign`, {
        params: { number },
      });
      return Success(response.data);
    } catch (error) {
      if (error.isAxiosError) {
        return Fail(error.response.data);
      }
      throw error;
    }
  };

  /**
   * @description Создать документ из шаблона для подписания.
   * @param {number} documentId Идентификатор шаблона.
   * @param {number} contractId Идентификатор договора.
   * @returns {Promise}
   */
  this.generateDocumentFromTemplateForSign = async (documentId, contractId) => {
    try {
      const response = await axios.get(`/api/documents/${documentId}/sign`, {
        params: { contract_id: contractId },
      });
      return Success(response.data);
    } catch (error) {
      if (error.isAxiosError) {
        return Fail(error.response.data);
      }
      throw error;
    }
  };

  /**
   * @description Создать Пакет из шаблона для подписания.
   * @param {PatientTemplate} template Объект шаблона пациента.
   * @returns {Promise}
   */
  this.generatePackageFromTemplateForSign = async (template) => {
    try {
      const data = template.toJSON();
      const response = await axios.post(`/api/packages/${template.id}/sign`, data, {
        headers: {
          'Content-Type': 'application/json',
        },
      });
      return Success(response.data);
    } catch (error) {
      if (error.isAxiosError) {
        return Fail(error.response.data);
      }
      throw error;
    }
  };
}
